import {
  type CommittedDelta,
  type DeltaTarget,
  type InputAnswer,
  isRoundOperation,
  labelAt,
  type PerspectiveRef,
  type ProposedDelta,
  type ThreadEntry,
  type Timeline,
} from "@pax/world";
import { ACTIVE_VIEWS, gameMasterDelta, perspectiveTarget, stateAfter } from "./world";
import type { WorldState } from "./world-state";

/** A player's order the jump carries out. */
interface CommittedAction {
  id: string;
  text: string;
  perspectiveHandle: string;
  userId: string;
}

/** Something a player did during the jump, which the game master answers in a batch. */
export type PlayerResponse = {
  perspectiveHandle: string;
  userId: string;
  revision: number;
} & (
  | { type: "input"; answer: InputAnswer }
  | { type: "intervention"; text: string }
  | { type: "thread"; threadId: string; entry: ThreadEntry }
);

/** How far the game master has answered a jump, as it records in its own private state. */
export interface JumpProgress {
  /** The round the jump ends, which names it. */
  round: number;
  /** The latest revision its batches have answered. */
  throughRevision: number;
  /** How many batches it has committed in the jump. */
  batches: number;
}

/** The game master's turn in the active jump: what it has been asked to answer so far. */
export interface JumpTurn {
  jump: { round: number; guidance?: string };
  /** 1 for the jump's opening batch, then one more for each batch the game master answered with. */
  turn: number;
  /** The orders this jump carries out. */
  actions: CommittedAction[];
  /** What players did during the jump that no batch has answered yet, oldest first. */
  responses: PlayerResponse[];
  /**
   * Every delta the next batch answers: for the opening batch, the actions and `jump.started`;
   * after that, whatever players wrote since the last batch.
   */
  deltas: CommittedDelta[];
  /** Whether a batch is owed: the opening one, or one answering `responses`. */
  owesBatch: boolean;
}

/**
 * The game master's turn in the active jump, or null when no jump is active.
 *
 * A jump owes its opening batch from `jump.started`. After that, each answer to an input, each
 * intervention, and each thread reply a player writes during the jump waits until `progress`, the
 * game master's own record, says a batch answered through its revision. A game master with no record of the jump, as
 * when it takes over mid-jump, goes by the committed batches instead. Players' own arrangements
 * and what they ask their advisor go on beside the jump and never wait on it.
 */
export function jumpTurn(
  record: readonly CommittedDelta[],
  progress: JumpProgress | null,
): JumpTurn | null {
  const actions = new Map<
    string,
    CommittedAction & { pending: boolean; submitted: CommittedDelta }
  >();
  let active:
    | (Omit<JumpTurn, "owesBatch" | "turn"> & {
        playersDeltas: CommittedDelta[];
        allResponses: PlayerResponse[];
        /** When the jump's batches were committed, and the latest revision in any of them. */
        batchCommits: Set<string>;
        batchedThrough: number | null;
      })
    | null = null;

  for (const delta of record) {
    const { operation, actor, target, responsibleUser } = delta;
    if (!partOfJumps(delta)) continue;
    if (isRoundOperation(operation) && !reachesSpectator(target)) continue;
    switch (operation.type) {
      case "action.submitted":
        if (actor.type === "perspective" && responsibleUser) {
          actions.set(operation.action.id, {
            ...operation.action,
            perspectiveHandle: actor.handle,
            userId: responsibleUser,
            pending: true,
            submitted: delta,
          });
        }
        continue;
      case "action.edited": {
        const action = actions.get(operation.id);
        if (action) action.text = operation.text;
        continue;
      }
      case "action.withdrawn":
        actions.delete(operation.id);
        continue;
      case "jump.started": {
        const pending = [...actions.values()].filter(action => action.pending);
        active = {
          jump: {
            round: operation.round,
            ...(operation.guidance ? { guidance: operation.guidance } : {}),
          },
          actions: pending.map(({ pending: _, submitted: __, ...action }) => action),
          responses: [],
          deltas: [],
          playersDeltas: [...pending.map(action => action.submitted), delta],
          allResponses: [],
          batchCommits: new Set(),
          batchedThrough: null,
        };
        for (const action of actions.values()) action.pending = false;
        continue;
      }
      case "jump.completed":
        active = null;
        continue;
    }
    if (!active) continue;
    if (actor.type === "gameMaster") {
      if (STORY_OPERATIONS.has(operation.type)) {
        active.batchedThrough = delta.revision;
        active.batchCommits.add(delta.committedAt);
      }
      continue;
    }
    if (actor.type !== "perspective" || !responsibleUser) continue;
    active.playersDeltas.push(delta);
    const from = { perspectiveHandle: actor.handle, userId: responsibleUser };
    if (operation.type === "input.answered") {
      active.allResponses.push({
        type: "input",
        ...from,
        revision: delta.revision,
        answer: operation.answer,
      });
    } else if (operation.type === "jump.intervened") {
      active.allResponses.push({
        type: "intervention",
        ...from,
        revision: delta.revision,
        text: operation.text,
      });
    } else if (operation.type === "thread.append") {
      active.allResponses.push({
        type: "thread",
        ...from,
        revision: delta.revision,
        threadId: operation.threadId,
        entry: operation.entry,
      });
    }
  }

  if (!active) return null;
  const { playersDeltas, allResponses, batchCommits, batchedThrough, ...turn } = active;
  const recorded = progress?.round === turn.jump.round ? progress : null;
  const answered =
    recorded ??
    (batchedThrough === null
      ? null
      : { throughRevision: batchedThrough, batches: batchCommits.size });
  const answeredThrough = answered?.throughRevision ?? null;
  const unanswered = (revision: number) => answeredThrough === null || revision > answeredThrough;
  const responses = allResponses.filter(response => unanswered(response.revision));
  return {
    ...turn,
    turn: 1 + (answered?.batches ?? 0),
    responses,
    deltas: playersDeltas.filter(delta => unanswered(delta.revision)),
    owesBatch: answeredThrough === null || responses.length > 0,
  };
}

/** The spectator's round deltas are the jumps; those to a perspective without users backfill it. */
function reachesSpectator(target: DeltaTarget): boolean {
  return (
    target.type === "activeViews" ||
    (target.type === "perspectives" && target.perspectives.some(ref => ref.type === "spectator"))
  );
}

/** Players' own arrangements and what they ask their advisor go on beside jumps, never in one. */
function partOfJumps({ operation, actor }: CommittedDelta): boolean {
  if (actor.type !== "perspective") return true;
  return (
    operation.type !== "thread.pinsSet" &&
    operation.type !== "board.arranged" &&
    operation.type !== "advisor.entryAdded"
  );
}

/** The record, the world it reduces to, and how far the game master has answered the jump. */
export interface JumpRecord {
  history: readonly CommittedDelta[];
  state: WorldState;
  answeredJump: JumpProgress | null;
}

/**
 * What the game master adds round `batch` when it ends the jump: `before` it, a withdrawal of every
 * input still waiting, so the batch may write on every timeline; `after` it, `jump.completed`, for
 * every perspective at once, at the tick the public timeline ends. Nothing when it goes on, or when
 * `batch` completes the jump itself.
 */
export function closingDeltas(
  { history, state, answeredJump }: JumpRecord,
  batch: readonly ProposedDelta[],
  ends: boolean,
): { before: ProposedDelta[]; after: ProposedDelta[] } {
  const none = { before: [], after: [] };
  if (!ends || batch.some(delta => delta.operation.type === "jump.completed")) return none;
  const turn = jumpTurn(history, answeredJump);
  if (!turn) return none;
  const before = Object.values(state.perspectives).flatMap(view =>
    view.awaits
      ? [gameMasterDelta(perspectiveTarget(view.perspective.handle), { type: "input.withdrawn" })]
      : [],
  );
  const ended = stateAfter(state, [...before, ...batch]);
  return {
    before,
    after: [
      gameMasterDelta(ACTIVE_VIEWS, {
        type: "jump.completed",
        roundCompleted: turn.jump.round,
        tick: ended.spectator.timeline.end,
      }),
    ],
  };
}

/**
 * Every timeline brought to the latest of their ends, where a jump then completes, and labelled
 * there: with the label the public timeline already has there, or else `label`. A timeline that
 * ends earlier is extended to it, and one that ends there without a label is clarified with one.
 */
export function boundaryDeltas(state: WorldState, label: string): ProposedDelta[] {
  const views: { ref: PerspectiveRef; timeline: Timeline }[] = [
    { ref: { type: "spectator" }, timeline: state.spectator.timeline },
    ...Object.keys(state.perspectives)
      .sort()
      .map(handle => ({
        ref: { type: "perspective" as const, handle },
        timeline: state.perspectives[handle].timeline,
      })),
  ];
  const end = Math.max(...views.map(view => view.timeline.end));
  const labels = [{ tick: end, date: labelAt(state.spectator.timeline.labels, end) ?? label }];
  const behind = targetReaching(views.filter(view => view.timeline.end < end));
  const unlabelled = targetReaching(
    views.filter(view => view.timeline.end === end && labelAt(view.timeline.labels, end) === null),
  );
  return [
    ...(behind
      ? [gameMasterDelta(behind, { type: "timeline.extended", extension: { end, labels } })]
      : []),
    ...(unlabelled
      ? [gameMasterDelta(unlabelled, { type: "timeline.clarified", clarification: { labels } })]
      : []),
  ];
}

/** A target reaching each of `views`, or null when there are none. */
function targetReaching(views: readonly { ref: PerspectiveRef }[]): DeltaTarget | null {
  const [first, ...rest] = views.map(view => view.ref);
  return first ? { type: "perspectives", perspectives: [first, ...rest] } : null;
}

/** What the game master writes and shows as a jump plays: each marks one of its batches. */
const STORY_OPERATIONS: ReadonlySet<string> = new Set([
  "timeline.extended",
  "timeline.clarified",
  "scene.shown",
  "input.asked",
]);
